/**
 * LandlordPicker — searchable landlord link for the work-report form (C3).
 *
 * Sixth thin wrapper over the shared ``AsyncCombobox`` (C0). It owns only
 * the landlord vocabulary and delegates debounce, paging, stale-response
 * protection, keyboard and theming to the base.
 *
 * Replaces a native <select> filled with ``fetchAdminLandlords("active")``
 * — the whole table, unbounded and unsearchable — whose option label read
 * ``l.name || l.company_name || l.id``. The API serializes no ``name``
 * field at all, so a private landlord without a company fell through to
 * the raw UUID. That is the "kaputtes Label" this picker removes.
 *
 * Rendered shape:
 *   Muster Immobilien AG
 *   Peter Muster · peter@example.test
 *
 * Server contract:
 *   GET /api/admin/landlords/picker-options?search=…&status&limit&offset
 *   GET /api/admin/landlords/{id}   for hydrating the current link
 *
 * The payload it feeds back is unchanged: a bare ``landlord_id`` string.
 */
import React from "react";

import AsyncCombobox from "../../../../components/ui/AsyncCombobox";
import {
  fetchAdminLandlord,
  fetchAdminLandlordsPage,
} from "../../../../api/adminData";

const PAGE_SIZE = 20;

/**
 * "Muster Immobilien AG" for a firm, the contact person for a private
 * landlord, then e-mail, then an honest placeholder. Never a UUID —
 * that was the pre-C3 fallback.
 * Exported for tests.
 */
export function landlordName(landlord) {
  const company = (landlord?.company_name || "").trim();
  if (company) return company;
  const contact = (landlord?.contact_name || "").trim();
  if (contact) return contact;
  const email = (landlord?.email || "").trim();
  if (email) return email;
  return "Vermieter ohne Namen";
}

/**
 * "Peter Muster · peter@example.test". The contact name is omitted when
 * it is already the primary line (private landlord without a company),
 * so nothing is printed twice. Returns null when nothing is left.
 * Exported for tests.
 */
export function landlordContact(landlord) {
  const company = (landlord?.company_name || "").trim();
  const contact = (landlord?.contact_name || "").trim();
  const email = (landlord?.email || "").trim();
  return (
    [company ? contact : null, email].filter(Boolean).join(" · ") || null
  );
}

/** Archived landlords stay selectable but read as secondary. */
function isArchived(landlord) {
  return Boolean(landlord?.deleted_at);
}

export default function LandlordPicker({ value, onChange, disabled = false }) {
  const [selected, setSelected] = React.useState(null);

  // Hydrate the CURRENT link by id (edit mode, and C1.1 prefill from a
  // task). An archived landlord — or one past page 1 — would otherwise
  // render as "nothing selected" and a save could drop the link.
  React.useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    if (selected && String(selected.id) === String(value)) return undefined;
    let cancelled = false;
    fetchAdminLandlord(value)
      .then((landlord) => {
        if (!cancelled && landlord) setSelected(landlord);
      })
      .catch(() => {
        // A deleted or inaccessible landlord must not break the form.
      });
    return () => {
      cancelled = true;
    };
  }, [value, selected]);

  const fetchPage = React.useCallback(
    ({ query, offset, limit }) =>
      fetchAdminLandlordsPage({
        search: query || undefined,
        // Archived landlords stay findable — the server ranks them last,
        // and an existing link to one must remain selectable.
        status: "all",
        limit,
        offset,
      }),
    []
  );

  const handleChange = React.useCallback(
    (id, item) => {
      setSelected(item);
      onChange(id);
    },
    [onChange]
  );

  return (
    <AsyncCombobox
      label="Vermieter (optional)"
      placeholder="— keine —"
      searchPlaceholder="Vermieter nach Firma, Name oder E-Mail suchen …"
      loadingText="Vermieter werden geladen …"
      emptyText="Keine passenden Vermieter gefunden."
      errorText="Vermieter konnten nicht geladen werden."
      value={value}
      selectedItem={selected}
      onChange={handleChange}
      fetchPage={fetchPage}
      getItemId={(l) => l.id}
      getItemLabel={(l) => landlordName(l)}
      pageSize={PAGE_SIZE}
      disabled={disabled}
      renderItem={(landlord) => (
        <span className="block min-w-0">
          <span
            title={landlordName(landlord)}
            className={
              "block truncate text-[13px] " +
              (isArchived(landlord)
                ? "font-normal text-muted-foreground"
                : "font-medium text-foreground")
            }
          >
            {landlordName(landlord)}
          </span>
          {landlordContact(landlord) ? (
            <span
              title={landlordContact(landlord)}
              className="block truncate text-[11px] text-muted-foreground"
            >
              {landlordContact(landlord)}
            </span>
          ) : null}
        </span>
      )}
    />
  );
}
